<template>
  <div class="invoices-table table-component p-2">
    <div class="mb-2" v-if="progressValue.overdue">
      <b-progress animated striped variant="primary">
        <b-progress-bar :value="progressValue.overdue" class="mb-0"
                        :label="$t('{name} - {progressValue}%', {name: $t('Sending overdue emails'), progressValue: progressValue.overdue})"/>
      </b-progress>
    </div>
    <div class="mb-2" v-if="progressValue.unpaid">
      <b-progress animated striped variant="primary">
        <b-progress-bar :value="progressValue.unpaid" class="mb-0"
                        :label="$t('{name} - {progressValue}%', {name: $t('Sending unpaid emails'), progressValue: progressValue.unpaid})"/>
      </b-progress>
    </div>
    <artisio-table :index="false" selectable ref="invoiceTable" :pagination="tableData.pagination"
                   :sort="tableData.ordering" :items="tableData.rows" :busy="tableData.loading" requires-min-height
                   :checkboxes="true" :search="tableData.search" :enable-simple-search="false" show-toggle-columns
                   :export-attributes="exportAttributes" :fields="tableFields" @paramsChanged="getTableData"
                   export-vuex-module-name="invoices" export-permission-name="export_invoice"
                   :extra-data="extraData" filter-toggle-string="invoices-table"
                   :enable-frontend-export="enableFrontendExport"
                   @update-toggle-columns="updateToggleColumns" @on-column-save="updateToggleColumns">
      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary" v-if="hideActionsDropdown"
                    v-permission="'change_invoice'">
          <b-dropdown-item @click="onNewInvoiceClick" v-if="!hideAction(ACTION_CREATE_NEW_INVOICE)">
            {{ $t('New Invoice') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onNewManualInvoiceClick" v-if="!hideAction(ACTION_CREATE_MANUAL_INVOICE)">
            {{ $t('New Manual Invoice') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onNewServiceInvoiceClick" v-if="!hideAction(ACTION_CREATE_SERVICE_INVOICE)">
            {{ $t('New Service Invoice') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_CREATE_CREDIT_NOTE)" @click="onCreateCreditNoteClick(selectedItems)"
                           :disabled="selectedItems.size === 0 || selectedItems.size > 1">
            {{ $t('New Credit Note') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onNewDepositInvoiceClick" v-if="!hideAction(ACTION_CREATE_DEPOSIT_INVOICE)">
            {{ $t('New Deposit Invoice') }}
          </b-dropdown-item>
          <b-dropdown-item :disabled="batchInvoiceGenerationProgress !== null" @click="onBatchInvoiceCreate"
                           v-if="!hideAction(ACTION_BATCH_INVOICE_GENERATE)">
            <div class="d-flex justify-content-between align-items-center">
              <spinner class="mr-2 d-inline-block" :show="batchInvoiceGenerationProgress !== null" spinner-size="1"
                       :with-text="false"/>
              <span>
                {{ $t('Generate Unpaid Invoices') }}
              </span>
            </div>
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item v-if="!hideAction(PREVIEW_ALL)" :disabled="selectedItems.size === 0"
                           @click="onPreviewAllClick(selectedItems)">
            {{ $t('Preview PDF(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_PAY_INVOICE)" @click="onInvoicesPayClick(selectedItems)"
                           :disabled="selectedItems.size === 0">
            {{ $t('Pay Invoice(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_APPROVE_AUTO_PAYMENT)"
                           @click="onApproveAutoPaymentClick(selectedItems)"
                           :disabled="!isAutoPaymentEnabled(selectedItems)">
            {{ $t('Approve Auto-Payment') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_REFUND_INVOICE)" @click="onInvoicesRefundClick(selectedItems)"
                           :disabled="selectedItems.size === 0">
            {{ $t('Refund Invoice(s)') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onGenerateBatchPaymentsClick(selectedItems)"
                           v-if="!hideAction(ACTION_GENERATE_INVOICE_BATCH_PAYMENTS)">
            {{ $t('Generate Batch Payments') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_COLLECTED)" :disabled="selectedItems.size === 0"
                           @click="onCollectedClick(selectedItems)">
            {{ $t('Mark item(s) Collected') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_APPROVE_INVOICES)" :disabled="selectedItems.size === 0"
                           @click="onApproveClick(selectedItems)">
            {{ $t('Approve Invoice(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_APPROVE_INVOICES)" :disabled="selectedItems.size === 0"
                           @click="onUndoApproveClick(selectedItems)">
            {{ $t('Undo Invoice(s) Approval') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="!hideAction(ACTION_SYNC_WITH_ACCOUNTING_SYSTEM) && isAccountingEnabled && !isManualAccountingSyncDisabled"
            @click="onSyncWithAccountingSystemClick()">
            {{ $t('Sync with Accounting System') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onBulkEditInvoicesClick(selectedItems)" v-if="!hideAction(ACTION_BULK_EDIT_INVOICES)"
                           :disabled="selectedItems.size === 0">
            {{ $t('Bulk Edit Invoices') }}
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <template v-if="enableAccountingExport && $hasPermission('export_invoice')">
            <b-dropdown-item @click="onExportClick('xlsx')">
              {{ $t('Export Invoices') }}
            </b-dropdown-item>
            <b-dropdown-item v-if="enableAccountingExport" @click="onExportClick('xlsx', true)">
              {{ $t('Export Invoice Payments') }}
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
          </template>
          <b-dropdown-item v-if="!hideAction(ACTION_SEND_UNPAID_EMAILS)" :disabled="selectedItems.size === 0"
                           @click="onSendUnpaidEmailClick(selectedItems)">
            {{ $t('Send Unpaid Email(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_SEND_UNPAID_EMAILS)" :disabled="selectedItems.size === 0"
                           @click="onResendInvoiceClick(selectedItems)">
            {{ $t('Resend Invoice(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_SEND_OVERDUE_EMAILS)" :disabled="selectedItems.size === 0"
                           @click="onSendOverdueEmailClick(selectedItems, true)">
            {{ $t('Send Overdue Email(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_SEND_SECOND_OVERDUE_EMAILS)" :disabled="selectedItems.size === 0"
                           @click="onSendOverdueEmailClick(selectedItems, false)">
            {{ $t('Send Second Overdue Email(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_SEND_THIRD_OVERDUE_EMAILS)" :disabled="selectedItems.size === 0"
                           @click="onSendOverdueEmailClick(selectedItems, false, true)">
            {{ $t('Send Third Overdue Email(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_SEND_CREATE_CREDIT_EMAILS)" :disabled="selectedItems.size === 0"
                           @click="onSendCreditNoteEmailClick(selectedItems)">
            {{ $t('Send Credit Note Email(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_SEND_COLLECTION_APPOINTMENT_NOTIFICATION)"
                           :disabled="selectedItems.size === 0"
                           @click="onSendCollectionAppointmentNotificationClick(selectedItems)">
            {{ $t('Send Collection Appointment Email(s)') }}
          </b-dropdown-item>
          <template v-if="!hideAction(ACTION_BATCH_SHIPPING_CHARGES)">
            <InvoiceBatchShippingCharges/>
          </template>
        </b-dropdown>
        <b-button variant="primary" @click="onMarkAsDispatchedClick(selectedItems)"
                  :disabled="selectedItems.size === 0" v-if="!hideAction(ACTION_MARK_INVOICE_DISPATCHED)">
          {{ $t("Mark as Dispatched") }}
        </b-button>
        <b-button variant="primary" class="ml-2" @click="onUnmarkAsDispatchedClick(selectedItems)"
                  :disabled="selectedItems.size === 0" v-if="!hideAction(ACTION_UNMARK_INVOICE_DISPATCHED)">
          {{ $t("Unmark as Dispatched") }}
        </b-button>
        <slot name="extra-actions" v-bind:selected-items="selectedItems"></slot>
      </template>
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="invoices-table">
          <invoice-search :exclude-search-fields="excludeSearchFields" :currency="sale ? sale.currency : null"
                          :exclude-types-from-filter="excludeTypesFromFilter"/>
        </search-collapse>
      </template>
      <template v-slot:head(collected)="tableFields">
        <b-popover target="collected-question-icon" triggers="hover" placement="left">
          {{ $t('[Number of items marked collected in the invoice] / [Number of total items in the invoice]') }}
        </b-popover>
        <span v-html="tableFields.label"></span>
      </template>
      <template slot="cell(sale_no)" slot-scope="{item: invoice}">
        <router-link v-if="invoice.auction && !enableContinuousAuction" v-permission="'view_auction'"
                     :to="{ name: 'sales.view', params: { id: invoice.auction.uuid }}">
          {{ invoice.auction.sale_no }}
        </router-link>
        <span v-else-if="invoice.auction && enableContinuousAuction">
          {{ invoice.auction.sale_no }}
        </span>
        <router-link v-else-if="invoice.deposit_sale_no && !enableContinuousAuction" v-permission="'view_auction'"
                     :to="{ name: 'sales.view', params: { id: invoice.deposit_reservations[0].auction_uuid }}">
          {{ invoice.deposit_sale_no }}
        </router-link>
        <live-sale-ref v-if="invoice.auction && invoice.auction.live_reference"
                       :href="invoice.auction.live_url"
                       :label="invoice.auction.live_reference"/>
      </template>
      <template slot="cell(type)" slot-scope="{item: invoice}">
        <span class="badge border">{{ invoice.type | removeUnderscores | toUpperCaseEveryWord | translate }}</span>
      </template>
      <template slot="cell(invoice_no)" slot-scope="{item: invoice}">
        <span v-if="disabledInvoice(invoice) || !$hasPermission('change_invoice')">
          {{ invoice.invoice_no }}
        </span>
        <a v-else href="javascript:void(0)"
           @click="onInvoiceEditClick(invoice)">
          {{ invoice.invoice_no }}
        </a>
      </template>
      <template slot="cell(date)" slot-scope="{item: invoice}">
        {{ invoice.date | toDate }}
        <template v-if="invoice.is_overdue">
          - {{ invoice.overdue_days_count }} day(s) overdue
        </template>
      </template>
      <template slot="cell(last_synced_at)" slot-scope="{item: invoice}">
        {{ invoice.last_synced_at | toDateTime }}
      </template>
      <template slot="cell(items)" slot-scope="{item: invoice}">
        {{ getDataObject(invoice).total_lots }}
      </template>
      <template slot="cell(amount)" slot-scope="{item: invoice}">
        {{ invoice.total_amount | asCurrency(invoice.currency ? invoice.currency.symbol : null) }} /
        {{ invoice.paid_amount | asCurrency(invoice.currency ? invoice.currency.symbol : null) }} /
        {{ invoice.outstanding_amount | asCurrency(invoice.currency ? invoice.currency.symbol : null) }}
      </template>
      <template slot="cell(customer_uuid)" slot-scope="{item: invoice}">
        <template v-if="invoice.customer">
          <div class="d-flex justify-content-between">
            <router-link :to="{ name: 'customers.dashboard', params: { id: invoice.customer.uuid }}">
              {{ invoice.customer.display_name }}
            </router-link>
            <red-flag-displayer :customer="invoice.customer"/>
          </div>
        </template>
      </template>
      <template slot="cell(total_buyer_vat)" slot-scope="{ item: invoice }">
        {{ invoice.total_buyer_vat | asCurrency(invoice.currency ? invoice.currency.symbol : 0) }}
      </template>

      <template slot="cell(customer_company_name)" slot-scope="{item: invoice}">
        <template v-if="invoice.customer">
          <router-link :to="{ name: 'customers.dashboard', params: { id: invoice.customer.uuid }}">
            {{ invoice.customer.company_name }}
          </router-link>
        </template>
      </template>
      <template slot="cell(customer_email)" slot-scope="{item: invoice}">
        <template v-if="invoice.customer">
          <router-link :to="{ name: 'customers.dashboard', params: { id: invoice.customer.uuid }}">
            {{ invoice.customer.email }}
          </router-link>
        </template>
      </template>
      <template slot="cell(tags)" slot-scope="{item: invoice}">
        <div v-if="invoice.customer && invoice.customer.tags">
          <span class="badge badge-info mr-2" v-for="tag in invoice.customer.tags.split(',')"
                :key="`customer-table-tag-${invoice.customer.uuid}-${tag}`">{{ tag }}</span>
        </div>
      </template>
      <template slot="cell(status)" slot-scope="{item: invoice}">
        <span v-if="invoice.status" v-as-status="invoice.status">
          {{ invoice.status | removeUnderscores | toUpperCaseFirst | translate }}
        </span>
        <span v-else v-as-status>{{ $t('N/A') }}</span>
      </template>
      <template slot="cell(shipping_status)" slot-scope="{item: invoice}">
        <span v-as-status="'dispatched'" v-if="invoice.is_dispatched">
          {{ $t('Dispatched') }}
        </span>
        <span v-as-status="invoice.shipping_status" v-else>
          {{ invoice.shipping_status ? invoice.shipping_status : $t('N/A') | removeUnderscores | toUpperCaseFirst | translate }}
        </span>
      </template>
      <template slot="cell(shipping_address_1)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_shipping_address">
          {{ invoice.invoice_shipping_address.address_1 }}
        </div>
      </template>
      <template slot="cell(shipping_address_2)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_shipping_address">
          {{ invoice.invoice_shipping_address.address_2 }}
        </div>
      </template>
      <template slot="cell(shipping_state)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_shipping_address">
          {{ invoice.invoice_shipping_address.state }}
        </div>
      </template>
      <template slot="cell(shipping_county)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_shipping_address">
          {{ invoice.invoice_shipping_address.county }}
        </div>
      </template>
      <template slot="cell(shipping_city)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_shipping_address">
          {{ invoice.invoice_shipping_address.city }}
        </div>
      </template>
      <template slot="cell(shipping_zip_code)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_shipping_address">
          {{ invoice.invoice_shipping_address.zip_code }}
        </div>
      </template>
      <template slot="cell(shipping_country)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_shipping_address && invoice.invoice_shipping_address.country">
          {{ invoice.invoice_shipping_address.country.name }}
        </div>
      </template>
      <template slot="cell(shipping_first_name)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_shipping_address">
          {{ invoice.invoice_shipping_address.first_name }}
        </div>
      </template>
      <template slot="cell(shipping_last_name)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_shipping_address">
          {{ invoice.invoice_shipping_address.last_name }}
        </div>
      </template>
      <template slot="cell(billing_address_1)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_billing_address">
          {{ invoice.invoice_billing_address.address_1 }}
        </div>
      </template>
      <template slot="cell(billing_address_2)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_billing_address">
          {{ invoice.invoice_billing_address.address_2 }}
        </div>
      </template>
      <template slot="cell(billing_state)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_billing_address">
          {{ invoice.invoice_billing_address.state }}
        </div>
      </template>
      <template slot="cell(billing_county)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_billing_address">
          {{ invoice.invoice_billing_address.county }}
        </div>
      </template>
      <template slot="cell(billing_city)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_billing_address">
          {{ invoice.invoice_billing_address.city }}
        </div>
      </template>
      <template slot="cell(billing_zip_code)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_billing_address">
          {{ invoice.invoice_billing_address.zip_code }}
        </div>
      </template>
      <template slot="cell(billing_country)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_billing_address && invoice.invoice_billing_address.country">
          {{ invoice.invoice_billing_address.country.name }}
        </div>
      </template>
      <template slot="cell(billing_first_name)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_billing_address">
          {{ invoice.invoice_billing_address.first_name }}
        </div>
      </template>
      <template slot="cell(billing_last_name)" slot-scope="{item: invoice}">
        <div v-if="invoice.invoice_billing_address">
          {{ invoice.invoice_billing_address.last_name }}
        </div>
      </template>
      <template slot="cell(collected)" slot-scope="{item: invoice}">
        {{ getDataObject(invoice).total_collected_lots }} / {{ getDataObject(invoice).total_lots }}
      </template>
      <template slot="cell(is_overdue)" slot-scope="{item: invoice}">
        <span v-html="$options.filters.asBadgeBoolean(invoice.is_overdue)"/>
      </template>
      <template slot="cell(overdue_notification_send_at)" slot-scope="{item: invoice}">
        <span v-if="invoice.overdue_notification_send_at">
          {{ invoice.overdue_notification_send_at | toDateTime }}
        </span>
      </template>
      <template slot="cell(unpaid_notification_send_at)" slot-scope="{item: invoice}">
        <span v-if="invoice.unpaid_notification_send_at">
          {{ invoice.unpaid_notification_send_at | toDateTime }}
        </span>
      </template>
      <template slot="cell(complaint_status)" slot-scope="{item: invoice}">
        <span v-as-status="invoice.complaint_status">
        {{ invoice.complaint_status ? invoice.complaint_status : $t('N/A') | removeUnderscores | toUpperCaseFirst }}
        </span>
      </template>
      <template slot="cell(computed_auto_payment_status)" slot-scope="{item: invoice}">
        <span v-if="invoice.computed_auto_payment_status"
              v-as-status="invoice.computed_auto_payment_status">
          {{ formatAutoPaymentStatus(invoice) }}
        </span>
        <span v-else v-as-status>{{ $t('N/A') }}</span>
      </template>
      <template slot="cell(payment_methods)" slot-scope="{item: invoice}">
        <template v-if="invoice.payment_methods && invoice.payment_methods.length > 0">
          <span :key="`payment-type-${invoice.uuid}-${method}`"
                class="mr-1" v-as-status="method" v-for="method in invoice.payment_methods">
            {{ method | asPaymentMethod }}
          </span>
        </template>
      </template>
      <template slot="cell(created_data)" slot-scope="{item: invoice}">
        {{ invoice.created_at | toDate }} by {{ invoice.created_by.display_name }}
      </template>
      <template slot="cell(updated_data)" slot-scope="{item: invoice}">
        {{ invoice.updated_at | toDate }} by {{ invoice.updated_by.display_name }}
      </template>
      <template slot="cell(approved)" slot-scope="{item: invoice}">
        <span v-if="invoice.approved" class="badge-success badge">{{ $t("Yes") }}</span>
        <span v-else class="badge-danger badge">{{ $t("No") }}</span>
      </template>
      <template slot="cell(collection_info_contract_status)" slot-scope="{item: invoice}">
        <span v-if="invoice.collection_info_contract_status" v-as-status="invoice.collection_info_contract_status">
          {{ invoice.collection_info_contract_status | removeUnderscores | toUpperCaseEveryWord | translate }}
        </span>
        <span v-as-status="invoice.collection_info_contract_status" v-else>{{ $t('N/A') }}</span>
      </template>
      <template slot="cell(paid_at)" slot-scope="{item: invoice}">
        {{ invoice.paid_at | toDate }}
      </template>
      <template slot="cell(collector)" slot-scope="{item: invoice}">
        <template v-if="invoice.collector">
          {{ invoice.collector }}
        </template>
      </template>
      <template slot="cell(buyer_group_account)" slot-scope="{item: invoice}" v-if="vendorBuyerGroupAccountsEnabled">
        <template v-if="invoice.auction">
          {{ invoice.auction.buyer_group_account }}
        </template>
      </template>
      <template slot="cell(branch)" slot-scope="{item: invoice}">
        <template v-if="invoice.branch">
          {{ invoice.branch.name }}
        </template>
      </template>
      <template slot="cell(actions)" slot-scope="{item: invoice}">
        <div class="position-relative">
          <b-dropdown right class="text-white" variant="primary" no-caret size="sm">
            <template #button-content>
              <font-awesome-icon icon="ellipsis-v" class="icon"/>
            </template>
            <b-dd-item @click="onInvoiceEditClick(invoice)" v-permission="'change_invoice'"
                       :disabled="disabledInvoice(invoice)">
              <font-awesome-icon icon="pen" style="font-size: .9em" class="mr-1"/>
              {{ $t('Edit Invoice') }}
            </b-dd-item>
            <b-dd-item v-permission="'view_invoice'" @click="onInvoiceViewClick(invoice)">
              <font-awesome-icon icon="file-pdf" style="font-size: .9em" class="mr-1"/>
              {{ $t('View Invoice') }}
            </b-dd-item>
            <b-dd-item :disabled="invoice.invoice_payments && invoice.invoice_payments.length === 0"
                       v-if="!hideActionButton(ACTION_SHOW_INVOICE_PAYMENTS_MODAL)" @click="onPaymentsClick(invoice)">
              <font-awesome-icon icon="wallet" style="font-size: .9em" class="mr-1"/>
              {{ $t('Payments') }}
            </b-dd-item>
            <b-dd-item
              :disabled="invoice.invoice_payments.length === 0 || invoice.status !== 'paid' || invoice.total_amount >= invoice.paid_amount"
              v-if="!hideActionButton(ACTION_SHOW_INVOICE_ALLOCATE_OVERPAYMENT)"
              @click="onAllocateOverpaymentClick(invoice)">
              <font-awesome-icon icon="wallet" style="font-size: .9em" class="mr-1"/>
              {{ $t('Allocate Overpayment') }}
            </b-dd-item>
            <b-dd-item
              v-if="!hideActionButton(ACTION_SHOW_INVOICE_SHIPPING_MODAL) && !['deposit', 'service'].includes(invoice.type)"
              @click="onViewShippingDetailsClick(invoice)">
              <font-awesome-icon icon="eye" style="font-size: .9em" class="mr-1"/>
              {{ $t('View Details') }}
            </b-dd-item>
            <b-dd-item
              v-if="!hideActionButton(ACTION_GENERATE_COLLECTION_NOTE) && ['auction', 'manual'].includes(invoice.type)"
              @click="onViewCollectionNoteClick(invoice)" v-permission="'change_invoice'">
              <font-awesome-icon icon="file-invoice" style="font-size: .9em" class="mr-1"/>
              {{ $t('Collection Note') }}
            </b-dd-item>
            <b-dd-item
              v-if="!hideAction(ACTION_CANCEL_INVOICE) && enableInvoiceCancellation"
              @click="onBatchInvoiceCancel(invoice)" v-permission="'change_invoice'">
              <font-awesome-icon icon="ban" style="font-size: .9em" class="mr-1"/>
              {{ $t('Cancel Invoice') }}
            </b-dd-item>
            <template v-for="input in showInvoiceExtraOptions" v-if="displayInvoiceExtraOptions(invoice)">
              <b-dd-item v-permission="'change_invoice'"
                         v-if="input.show_in_actions && input.does_not_change_amount"
                         @click="onInvoiceToggleExtraInfo(invoice, input)"
                         :key="`invoice-extra-info-checkboxes-${input.key}`">
                <font-awesome-icon icon="toggle-on" style="font-size: .9em" class="mr-1"
                                   v-if="invoice.extra_info && invoice.extra_info[input.key]"/>
                <font-awesome-icon v-else icon="toggle-off" style="font-size: .9em" class="mr-1"/>
                {{ input.label }}
              </b-dd-item>
            </template>
            <table-notes-button v-if="!hideActionButton(ACTION_SHOW_LIST_NOTES_MODAL)" :item="invoice"
                                :source="SOURCE_INVOICES_TABLE" object-name="invoice" is-dd-item
                                plural-object-name="invoices" change-permission="change_invoice"
                                :total-notes="invoice.total_notes"
                                delete-permission="change_invoice" :hide-footer="!$hasPermission('change_invoice')"/>
            <b-dd-item v-if="!hideActionButton(ACTION_SHOW_INVOICE_ACTIVITIES_MODAL)"
                       @click="onViewActivitiesClick(invoice)">
              <font-awesome-icon icon="stream" style="font-size: .9em" class="mr-1"/>
              {{ $t('Activities') }}
            </b-dd-item>

          </b-dropdown>
          <b-badge v-if="invoice && invoice.total_notes" class="total-notes">
            {{ invoice.total_notes }}
          </b-badge>
        </div>
      </template>
      <template slot="cell(transaction_history)" slot-scope="{item: invoice}">
        <exports :item="invoice" current-field="invoice_tr_history" :on-show-more-click="onShowTransactionMoreClick"/>
      </template>
      <template slot="cell(payment_history)" slot-scope="{item: invoice}">
        <exports :item="invoice" current-field="invoice_payments" :on-show-more-click="onShowPaymentMoreClick"/>
      </template>
      <template slot="cell(identity_verification)" slot-scope="{item: invoice}">
        {{ invoice.customer.term ? invoice.customer.term.identity_verification : '' | toUpperCaseFirst }}
      </template>

      <template slot="cell(kyc_status_verification)" slot-scope="{item: invoice}">
        {{ invoice.customer.term ? invoice.customer.term.kyc_status_verification : '' | toUpperCaseFirst }}
      </template>

      <template slot="cell(credited_invoice)" slot-scope="{item: invoice}">
        <a v-if="invoice.credited_invoice" href="javascript:void(0)"
           @click="onInvoiceViewClick(invoice.credited_invoice, true)">
          {{ invoice.credited_invoice.invoice_no }}
        </a>
      </template>

      <template slot="cell(credit_note)" slot-scope="{item: invoice}">
        <a v-if="invoice.credit_note" href="javascript:void(0)"
           @click="onInvoiceViewClick(invoice.credit_note, true)">
          {{ invoice.credit_note.invoice_no }}
        </a>
      </template>

      <template slot="cell(client_tier)" slot-scope="{item: invoice}">
        <client-tier-display-widget :customer="invoice.customer" v-if="invoice.customer"/>
      </template>

      <template slot="cell(exclude_from_auto_payments)" slot-scope="{item: invoice}">
        <span v-html="$options.filters.asBadgeBoolean(invoice.exclude_from_auto_payments)"
              v-if="invoice.type !== 'credit_note'"/>
      </template>

      <template slot="cell(collection_appointment_notification_sent_at)" slot-scope="{item: invoice}">
        <span v-if="invoice.collection_appointment_notification_sent_at">
          {{ invoice.collection_appointment_notification_sent_at | toDateTime }}
        </span>
      </template>

      <template slot="cell(sale_sources)" slot-scope="{item: invoice}">
        <div v-if="invoice.sale_sources">
          <div v-for="(count, sourceOfSale) in invoice.sale_sources" :key="`${invoice.uuid}-${sourceOfSale}`">
            {{ sourceOfSale | removeUnderscores | toUpperCaseEveryWord }} - {{ count }}
          </div>
        </div>
      </template>

      <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                v-for="dynamicField in toggleColumns.dynamicFields">
        <span
          v-if="item.customer.identification && item.customer.identification.dynamic_fields && item.customer.identification.dynamic_fields[dynamicField]"
          v-html="$options.filters.asDynamicField(item.customer.identification.dynamic_fields[dynamicField], field.type, field.choices)">
        </span>
      </template>


      <template slot="cell(total_amount)" slot-scope="{item: invoice}">
        {{ invoice.total_amount | asCurrency(invoice.currency ? invoice.currency.symbol : null) }}
      </template>

      <template slot="cell(paid_amount)" slot-scope="{item: invoice}">
        {{ invoice.paid_amount | asCurrency(invoice.currency ? invoice.currency.symbol : null) }}
      </template>

      <template slot="cell(outstanding_amount)" slot-scope="{item: invoice}">
        {{ invoice.outstanding_amount | asCurrency(invoice.currency ? invoice.currency.symbol : null) }}
      </template>

    </artisio-table>
    <invoice-activities-modal/>
    <continuous-auction-invoice-modal/>
    <payment-list-modal/>
    <shipping-modal/>
    <mark-items-collected-modal/>
    <cancel-invoice-modal v-if="cancelModal"/>
    <date-range-modal :submit-handler="onDateRangeSubmit" :title="$t(`Invoice Date Range`)" ref="date-range-modal"/>
    <credit-note-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import {
    ACTION_BATCH_INVOICE_GENERATE,
    ACTION_CANCEL_INVOICE,
    ACTION_COLLECTED,
    ACTION_CREATE_CREDIT_NOTE,
    ACTION_CREATE_DEPOSIT_INVOICE,
    ACTION_CREATE_MANUAL_INVOICE,
    ACTION_CREATE_NEW_INVOICE,
    ACTION_MARK_INVOICE_DISPATCHED,
    ACTION_PAY_INVOICE,
    ACTION_REFUND_INVOICE,
    ACTION_SEND_CREATE_CREDIT_EMAILS,
    ACTION_SEND_OVERDUE_EMAILS,
    ACTION_SEND_SECOND_OVERDUE_EMAILS,
    ACTION_SEND_THIRD_OVERDUE_EMAILS,
    ACTION_SEND_UNPAID_EMAILS,
    ACTION_SHOW_INVOICE_ACTIVITIES_MODAL,
    ACTION_SHOW_INVOICE_PAYMENTS_MODAL,
    ACTION_SHOW_INVOICE_SHIPPING_MODAL,
    ACTION_SHOW_LIST_NOTES_MODAL,
    ACTION_SYNC_WITH_ACCOUNTING_SYSTEM,
    ACTION_UNMARK_INVOICE_DISPATCHED,
    INVOICE_TYPE_CREDIT_NOTE,
    INVOICE_TYPE_AUCTION,
    SOURCE_INVOICES_TABLE,
    WS_ACCOUNTING_SYNC_PROGRESS,
    WS_BATCH_INVOICE_GENERATION_PROGRESS,
    WS_INVOICES_MERGED_PDF_PREVIEW_GENERATION_PROGRESS,
    WS_ON_OVERDUE_NOTIFICATION_PROGRESS,
    WS_ON_UNPAID_NOTIFICATION_PROGRESS,
    INVOICE_TABLE_NAME,
    ACTION_SHOW_CANCEL_INVOICE_MODAL,
    PREVIEW_ALL,
    ACTION_CREATE_SERVICE_INVOICE,
    ACTION_GENERATE_COLLECTION_NOTE,
    ACTION_APPROVE_INVOICES,
    ACTION_APPROVE_AUTO_PAYMENT,
    SOURCE_INVOICE_TRANSACTION_EXPORTS,
    SOURCE_INVOICE_PAYMENT_EXPORTS,
    ACTION_BULK_EDIT_INVOICES,
    ACTION_SHOW_INVOICE_ALLOCATE_OVERPAYMENT,
    ACTION_GENERATE_INVOICE_BATCH_PAYMENTS,
    ACTION_BATCH_SHIPPING_CHARGES,
    WS_BATCH_AUTO_PAYMENT_PROGRESS,
    ACTION_SEND_COLLECTION_APPOINTMENT_NOTIFICATION
  } from "../constants";
  import InvoicesService from "./InvoicesService";
  import PaymentListModal from "./PaymentListModal";
  import i18n from "../../shared/i18n";
  import InvoiceSearch from "./InvoiceSearch";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import ShippingModal from "../finance/shipping/ShippingModal"
  import MarkItemsCollectedModal from "@/modules/invoices/MarkItemsCollectedModal";
  import LiveSaleRef from "@/core/components/common/LiveSaleRef";
  import Spinner from "@/core/components/common/Spinner";
  import DateRangeModal from "../../core/components/date-range-picker-modal/DateRangeModal"
  import ContinuousAuctionInvoiceModal from "./ContinuousAuctionInvoiceModal";
  import InvoiceActivitiesModal from "@/modules/invoices/InvoiceActivitiesModal";
  import AccountingService from "../setup/accounting/AccountingService";
  import NotificationPdfPreviewService from "../../core/services/notificationPdfPreview.service";
  import CreditNoteModal from "./CreditNoteModal";
  import _ from "lodash";
  import TableNotesButton from "@/modules/notes/TableNotesButton";
  import Exports from "@/core/components/exports/Exports.vue";
  import ExportsService from "@/core/services/exports.service";
  import CancelInvoiceModal from "@/modules/invoices/CancelInvoiceModal.vue";
  import {Decimal} from 'decimal.js';
  import LotsService from "@/modules/lots/LotsService";
  import helperService from "@/core/services/helper.service";
  import InvoiceBatchShippingCharges from "@/modules/invoices/InvoiceBatchShippingCharges.vue";
  import InventoryService from "@/modules/inventory/InventoryService";
  import ClientTierDisplayWidget from "@/core/widgets/ClientTierDisplayWidget.vue";
  import RedFlagDisplayer from "@/modules/customer/RedFlagDisplayer.vue";

  const {mapActions, mapState} = createNamespacedHelpers('invoices');
  const {mapActions: activitiesMapActions} = createNamespacedHelpers('activities');
  const {mapActions: mapActionsGlobal, mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapActions: collectionsMapActions} = createNamespacedHelpers('collections');

  export default {
    name: "InvoicesTable",
    components: {
      RedFlagDisplayer,
      ClientTierDisplayWidget,
      InvoiceBatchShippingCharges,
      CancelInvoiceModal,
      TableNotesButton,
      CreditNoteModal,
      ContinuousAuctionInvoiceModal,
      Spinner,
      LiveSaleRef, MarkItemsCollectedModal, DateRangeModal,
      SearchCollapse, InvoiceSearch,
      PaymentListModal, ArtisioTable, ShippingModal, InvoiceActivitiesModal,
      Exports,
    },
    props: {
      hideActions: {
        type: [Boolean, Array],
        default: () => []
      },
      hideActionButtons: {
        type: [Boolean, Array],
        default: () => []
      },
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      excludeTypesFromFilter: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
      accountingSystemParams: {
        type: Object,
        default: () => {
        }
      },
      enableFrontendExport: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        ACTION_SEND_OVERDUE_EMAILS: ACTION_SEND_OVERDUE_EMAILS,
        ACTION_SEND_SECOND_OVERDUE_EMAILS: ACTION_SEND_SECOND_OVERDUE_EMAILS,
        ACTION_SEND_THIRD_OVERDUE_EMAILS: ACTION_SEND_THIRD_OVERDUE_EMAILS,
        ACTION_SEND_UNPAID_EMAILS: ACTION_SEND_UNPAID_EMAILS,
        SOURCE_INVOICES_TABLE: SOURCE_INVOICES_TABLE,
        ACTION_CREATE_NEW_INVOICE: ACTION_CREATE_NEW_INVOICE,
        ACTION_CREATE_MANUAL_INVOICE: ACTION_CREATE_MANUAL_INVOICE,
        ACTION_CREATE_SERVICE_INVOICE: ACTION_CREATE_SERVICE_INVOICE,
        ACTION_CREATE_DEPOSIT_INVOICE: ACTION_CREATE_DEPOSIT_INVOICE,
        ACTION_BATCH_INVOICE_GENERATE: ACTION_BATCH_INVOICE_GENERATE,
        ACTION_CANCEL_INVOICE: ACTION_CANCEL_INVOICE,
        ACTION_PAY_INVOICE: ACTION_PAY_INVOICE,
        ACTION_REFUND_INVOICE: ACTION_REFUND_INVOICE,
        ACTION_MARK_INVOICE_DISPATCHED: ACTION_MARK_INVOICE_DISPATCHED,
        ACTION_UNMARK_INVOICE_DISPATCHED: ACTION_UNMARK_INVOICE_DISPATCHED,
        ACTION_SHOW_INVOICE_PAYMENTS_MODAL: ACTION_SHOW_INVOICE_PAYMENTS_MODAL,
        ACTION_SHOW_INVOICE_SHIPPING_MODAL: ACTION_SHOW_INVOICE_SHIPPING_MODAL,
        ACTION_COLLECTED: ACTION_COLLECTED,
        ACTION_APPROVE_INVOICES: ACTION_APPROVE_INVOICES,
        ACTION_SYNC_WITH_ACCOUNTING_SYSTEM: ACTION_SYNC_WITH_ACCOUNTING_SYSTEM,
        ACTION_SHOW_LIST_NOTES_MODAL: ACTION_SHOW_LIST_NOTES_MODAL,
        ACTION_SHOW_INVOICE_ACTIVITIES_MODAL: ACTION_SHOW_INVOICE_ACTIVITIES_MODAL,
        PREVIEW_ALL: PREVIEW_ALL,
        ACTION_CREATE_CREDIT_NOTE: ACTION_CREATE_CREDIT_NOTE,
        ACTION_SEND_CREATE_CREDIT_EMAILS: ACTION_SEND_CREATE_CREDIT_EMAILS,
        ACTION_GENERATE_COLLECTION_NOTE: ACTION_GENERATE_COLLECTION_NOTE,
        ACTION_SHOW_CANCEL_INVOICE_MODAL, ACTION_BULK_EDIT_INVOICES,
        ACTION_SHOW_INVOICE_ALLOCATE_OVERPAYMENT, ACTION_GENERATE_INVOICE_BATCH_PAYMENTS, ACTION_BATCH_SHIPPING_CHARGES,
        ACTION_APPROVE_AUTO_PAYMENT,
        ACTION_SEND_COLLECTION_APPOINTMENT_NOTIFICATION,
        batchInvoiceGenerationProgress: null,
        progressValue: {
          overdue: null,
          unpaid: null
        },
        autoPaymentProgress: null,
        exportAttributes: {
          invoice_no: i18n.t('Invoice'),
          'customer.display_name': i18n.t('Customer'),
          'customer.email': i18n.t('Email'),
          total_amount: i18n.t('Total Amount'),
          total_lots: i18n.t('Total Items'),
          status: i18n.t('Status'),
        },
        processIdToProgressMap: {},
        pdfPreviewRequestId: null,
      }
    },
    computed: {
      ...mapState({
        cancelModal: state => state.cancelInvoiceModal.show,
        tableData: state => state.tableData[state.tableSource],
        sale: state => state.currentSale,
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource
      }),
      ...mapStateGlobal({
        isAccountingEnabled: state => state.isAccountingEnabled,
        isManualAccountingSyncDisabled: state => state.isManualAccountingSyncDisabled,
        enableContinuousAuction: state => state.enableContinuousAuction,
        currentUser: state => state.currentUser,
        enableInvoiceCancellation: state => state.enableInvoiceCancellation,
        enableAccountingExport: state => state.enableAccountingExport,
        showInvoiceExtraOptions: state => state.showInvoiceExtraOptions,
        vendorBuyerGroupAccountsEnabled: state => state.vendorBuyerGroupAccountsEnabled,
      }),
      computedExcludeColumns() {
        const excludeColumns = _.cloneDeep(this.excludeColumns);
        if (this.hideAction(ACTION_SYNC_WITH_ACCOUNTING_SYSTEM) || !this.isAccountingEnabled) {
          excludeColumns.push('last_synced_at');
        }
        if (!this.vendorBuyerGroupAccountsEnabled) {
          excludeColumns.push('buyer_group_account');
        }
        return excludeColumns;
      },
      tableFields() {
        return _.cloneDeep(this.toggleColumns.fields)
          .filter(field => !this.computedExcludeColumns.includes(field.key) && field.isVisible)
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.computedExcludeColumns,
          tableName: `${INVOICE_TABLE_NAME}.${this.tableSource}`,
        }
      },
      hideActionsDropdown() {
        const actionsToHide = [
          ACTION_CREATE_NEW_INVOICE,
          ACTION_CREATE_MANUAL_INVOICE,
          ACTION_CREATE_DEPOSIT_INVOICE,
          ACTION_BATCH_INVOICE_GENERATE,
          ACTION_CANCEL_INVOICE,
          ACTION_PAY_INVOICE,
          ACTION_REFUND_INVOICE,
          ACTION_SEND_OVERDUE_EMAILS,
          ACTION_SEND_UNPAID_EMAILS,
          ACTION_SYNC_WITH_ACCOUNTING_SYSTEM,
        ];
        for (let item of actionsToHide) {
          if (!this.hideActions.includes(item)) {
            return true
          }
        }
        return false
      }
    },
    methods: {
      ...mapActions([
        'getTableData', 'showModal', 'showManualInvoiceModal', 'showDepositInvoiceModal',
        'getModalObject', 'showPaymentModal', 'showPaymentListModal', 'getManualInvoiceModalObject', 'showShippingModal', 'getShippingModalData', 'markAllItemsCollected',
        'showMarkItemsCollectedModal', 'getMarkItemsCollectedModalObject', 'showDateRangeModal', 'hideDateRangeModal',
        'showContinuousAuctionInvoiceModal', 'updateToggleColumns', 'showCreditNoteModal', 'getCreditNoteInvoiceModalObject',
        'showServiceInvoiceModal', 'getServiceInvoiceModalObject',
        'showTransactionExportsModal', 'showPaymentExportsModal', 'showCancelInvoiceModal', 'showBulkEditInvoicesModal',
        'showAllocatePaymentModal', 'showInvoiceBatchPaymentModal', 'getDepositInvoiceModalObject'
      ]),
      ...activitiesMapActions(['showActivitiesModal', 'setActivitiesParams']),
      ...mapActionsGlobal([
        'addProgress',
        'removeProgress',
        'updateProgressValue',
        'showDateRangeModal',
        'showGeneralContractModal',
        'getGeneralContractModalData',
      ]),
      ...collectionsMapActions([
        'showCollectionsModal',
      ]),
      displayInvoiceExtraOptions(invoice) {
        return ['auction', 'manual'].indexOf(invoice.type) >= 0;
      },
      hideAction(actionName) {
        if (typeof this.hideActions === 'boolean') {
          // Generally hides/shows all actions
          return this.hideActions;
        } else if (typeof actionName === 'string') {
          // Used when specific action is checked
          return this.hideActions.includes(actionName);
        } else if (Array.isArray(actionName)) {
          // Used when one element is dependent on at least one action
          for (let name of actionName) {
            if (!this.hideActions.includes(name)) {
              return false;
            }
          }
          return true;
        }
        return this.hideActions;
      },
      hideActionButton(actionName) {
        if (typeof this.hideActionButtons === 'boolean') {
          // Generally hides/shows all actions
          return this.hideActionButtons;
        } else if (typeof actionName === 'string') {
          // Used when specific action is checked
          return this.hideActionButtons.includes(actionName);
        } else if (Array.isArray(actionName)) {
          // Used when one element is dependent on at least one action
          for (let name of actionName) {
            if (!this.hideActionButtons.includes(name)) {
              return false;
            }
          }
          return true;
        }
        return this.hideActionButtons;
      },
      async onInvoiceViewClick(invoice, onlyView = false) {
        await InvoicesService.viewInvoicePdf(this, invoice, onlyView, this.onMarkItemsCollectedClick)
      },
      onViewShippingDetailsClick(invoice) {
        this.getShippingModalData(invoice);
        this.showShippingModal({invoice});
      },
      async onViewCollectionNoteClick(invoice) {
        if (['cancelled', 'credited'].includes(invoice.status)) {
          let statusDisplay = {
            cancelled: this.$i18n.t('Cancelled'),
            credited: this.$i18n.t('Credited')
          }[invoice.status] || invoice.status
          this.$errorDialog(
            this.$i18n.t('A Collection Note cannot be created for invoice {invoice_no} because its status is {status}. Collection Notes are only available for active invoices.', {
              invoice_no: invoice.invoice_no,
              status: statusDisplay
            }),
            this.$i18n.t('Collection Note not available')
          )
          return
        }
        if (invoice.status === 'unpaid') {
          let success = await this.$confirm(
            this.$i18n.t('Selected invoice has not been paid, are you sure you would like to proceed?'),
            null,
            null,
            null,
            this.$i18n.t('Please Confirm'),
            this.$i18n.t('Yes'),
            this.$i18n.t('No'),
            null,
            {
              label: this.$i18n.t("To confirm collection note generation, type 'CONFIRM' in the text input field"),
              text: 'CONFIRM'
            }
          )
          if (!success) {
            return;
          }
        }

        let response = await InvoicesService.getInvoice(invoice.uuid, {
          expand: [
            'collection_info_contract_status',
            'financial_items.inventory.department',
            'financial_items.lot.inventory.department',
          ].join(',')
        })

        let showGeneralContractModal = async (data = {}) => {
          let response = await this.getGeneralContractModalData({
            object: invoice,
            fileType: 'collection_note',
            objectName: 'invoice',
            extra_data: data
          });
          if (!response.success) {
            this.$errorDialog(
              (response.body && response.body.non_field_errors && response.body.non_field_errors[0]) || this.$i18n.t('Something went wrong'),
              this.$i18n.t('Collection Note not available')
            )
            return response
          }
          this.showGeneralContractModal({
            source: SOURCE_INVOICES_TABLE,
            object: invoice,
            fileType: 'collection_note',
            objectName: 'invoice',
            refreshOnSubmit: true,
            modalTitle: this.$i18n.t("Collection Note")
          })
          return response
        }

        if (response.success) {
          if (!response.body.collection_info_contract_status) {

            let tmpItems = []
            for (let fi of response.body.financial_items) {
              if (fi.lot) {
                tmpItems.push({
                  department: fi.lot.inventory.department,
                  stock_no: fi.lot.inventory.stock_no,
                  dynamic_fields: fi.lot.inventory.dynamic_fields
                })
              } else if (fi.inventory) {
                tmpItems.push({
                  department: fi.inventory.department,
                  stock_no: fi.inventory.stock_no,
                  dynamic_fields: fi.inventory.dynamic_fields
                })
              }
            }

            let tmpSuccess = await InventoryService.warnOnCollectionForDynamicFields(this, tmpItems, false)
            if (!tmpSuccess) {
              return
            }

            this.showCollectionsModal({
              customer_uuid: invoice.customer_uuid,
              source: 'collection_note',
              handler: showGeneralContractModal,
              title: this.$i18n.t('Collection Note')
            })
          } else {
            showGeneralContractModal()
          }
        } else {
          this.$errorDialog(this.$i18n.t('Error while retrieving invoice information. Please, try again later'))
        }


      },
      async onInvoiceEditClick(invoice) {
        if (invoice.type === 'auction') {
          if (this.enableContinuousAuction) {
            this.showContinuousAuctionInvoiceModal({
              source: SOURCE_INVOICES_TABLE,
              sale: invoice.auction
            });
          } else {
            this.showModal({
              source: SOURCE_INVOICES_TABLE,
              sale: this.sale ? this.sale : invoice.auction
            });
          }
          this.getModalObject({uuid: invoice.uuid});
        } else if (invoice.type === 'manual') {
          this.showManualInvoiceModal({
            source: SOURCE_INVOICES_TABLE,
            customer: this.tableData.customer
          })
          this.getManualInvoiceModalObject({uuid: invoice.uuid});
        } else if (invoice.type === 'deposit') {
          this.showDepositInvoiceModal({
            source: SOURCE_INVOICES_TABLE,
            customer: this.tableData.customer
          })
          this.getDepositInvoiceModalObject({uuid: invoice.uuid});
        } else if (invoice.type === INVOICE_TYPE_CREDIT_NOTE) {
          this.showCreditNoteModal({
            source: SOURCE_INVOICES_TABLE,
            object: invoice,
          })
          this.getCreditNoteInvoiceModalObject({invoice});
        } else if (invoice.type === 'service') {
          this.showServiceInvoiceModal({
            source: SOURCE_INVOICES_TABLE,
            customer: this.tableData.customer
          })
          this.getServiceInvoiceModalObject({uuid: invoice.uuid});
        }
      },
      onNewInvoiceClick() {
        if (this.enableContinuousAuction) {
          this.showContinuousAuctionInvoiceModal({source: SOURCE_INVOICES_TABLE,});
        } else {
          this.showModal({
            source: SOURCE_INVOICES_TABLE,
            sale: this.sale
          });
        }
      },
      onNewDepositInvoiceClick() {
        this.showDepositInvoiceModal({
          source: SOURCE_INVOICES_TABLE,
          customer: this.tableData.customer
        })
      },
      onNewManualInvoiceClick() {
        this.showManualInvoiceModal({
          source: SOURCE_INVOICES_TABLE,
          customer: this.tableData.customer
        })
      },
      onBulkEditInvoicesClick(invoices) {
        for (let invoice of invoices) {
          if (invoice.is_not_editable) {
            this.$toast('danger', this.$i18n.t('You can not edit legacy invoice'));
            return;
          }
          if (invoice.is_credited) {
            this.$toast('danger', this.$i18n.t('You can not bulk edit credited invoices'));
            return;
          }
          if (invoice.is_credit_note) {
            this.$toast('danger', this.$i18n.t('You can not bulk edit credit notes'));
            return;
          }
          if (invoice.status === 'cancelled') {
            this.$toast('danger', this.$i18n.t('You can not bulk edit cancelled invoices'));
            return;
          }
          if (invoice.type === 'deposit') {
            this.$toast('danger', this.$i18n.t('You can not bulk edit deposit invoices'));
            return;
          }
        }
        this.showBulkEditInvoicesModal({invoices: Array.from(invoices)})
      },
      onNewServiceInvoiceClick() {
        this.showServiceInvoiceModal({
          source: SOURCE_INVOICES_TABLE,
          customer: this.tableData.customer
        })
      },
      async onBatchInvoiceCreate() {
        const invoiceGenerationHandler = async (generationMode) => {
          let response = await InvoicesService.generateSingleBatchInvoices(this.sale.sale_no, {'single_generation': generationMode});
          if (response.success) {
            await this.addBatchInvoiceGenerationProgressIfNotExist();
          }
          return response;
        }

        this.$confirm(
          this.$i18n.t('Are you sure you want to generate invoices for all winning bidders?'),
          this.$i18n.t("Unpaid invoices generation has successfully started"),
          invoiceGenerationHandler,
          false,
          undefined,
          i18n.t("Generate Grouped Invoices"),
          i18n.t("Cancel"),
          [{
            handler: invoiceGenerationHandler,
            handlerParams: true,
            submitBtnText: i18n.t("Generate Single Invoices"),
            successMessage: i18n.t("Unpaid invoices generation has successfully started")
          }]
        );
      },
      async onInvoicesPayClick(items) {
        const tmpInvoice = items.values().next().value;
        let customerUuid = tmpInvoice.customer_uuid;
        let currencyUuid = tmpInvoice.currency_uuid;

        for (let item of items) {
          if (item.status === 'cancelled') {
            this.$errorDialog(this.$i18n.t('Selected invoice(s) are already cancelled. You can not pay for cancelled invoice.'));
            return;
          }
          if (item.customer_uuid !== customerUuid) {
            this.$errorDialog(this.$i18n.t("You can not process one payment for different customers"));
            return;
          }
          if (item.currency_uuid !== currencyUuid) {
            this.$errorDialog(this.$i18n.t("You can not process one payment for different currencies"));
            return;
          }
          if (item.is_credit_note) {
            this.$toast('danger', this.$i18n.t('You can not pay for credit note invoice'));
            return;
          }
          if (item.is_credited) {
            this.$toast('danger', this.$i18n.t('You can not pay for credited invoice'));
            return;
          }
        }
        this.showPaymentModal({
          source: SOURCE_INVOICES_TABLE,
          invoices: [...items],
          type: 'payment'
        });
      },
      async onInvoicesRefundClick(items) {
        // todo check invoice statuses
        const tmpInvoice = items.values().next().value;
        let customerUuid = tmpInvoice.customer_uuid;
        let currencyUuid = tmpInvoice.currency_uuid;

        for (let item of items) {
          if (item.customer_uuid !== customerUuid) {
            this.$errorDialog(this.$i18n.t("You can not process one refund for different customers"));
            return;
          }
          if (item.is_credit_note) {
            this.$toast('danger', this.$i18n.t('You can not refund for credit note invoice'));
            return;
          }
          if (item.is_credited) {
            this.$toast('danger', this.$i18n.t('You can not refund for credited invoice'));
            return;
          }
          if (item.currency_uuid !== currencyUuid) {
            this.$errorDialog(this.$i18n.t("You can not process one refund for different currencies"));
            return;
          }
        }
        this.showPaymentModal({
          source: SOURCE_INVOICES_TABLE,
          invoices: [...items],
          type: 'refund'
        });
      },
      async onBatchInvoiceCancel(invoice) {
        let items = [invoice]
        let invoices_types = new Set()
        items.forEach(el => invoices_types.add(el.type))
        if (invoices_types.size > 1 && invoices_types.has('auction')) {
          this.$toast('danger', this.$i18n.t('Batch cancellation is not allowed for different types of invoices. Please select same type of invoices (either Auction or Manual/Deposit/Service) and try again.'));
          return;
        }
        for (let item of items) {
          if (item.status === 'cancelled') {
            this.$toast('danger', this.$i18n.t('Selected invoice(s) are already cancelled.'));
            return;
          }
          if (item.is_legacy) {
            this.$toast('danger', this.$i18n.t('You cannot cancel legacy invoice'));
            return;
          }
          if (item.is_credit_note) {
            this.$toast('danger', this.$i18n.t('You can not cancel credit note invoice'));
            return;
          }
          if (item.is_credited) {
            this.$toast('danger', this.$i18n.t('You can not cancel credited invoice'));
            return;
          }
          if (item.invoice_payments && item.invoice_payments.length > 0) {
            let invoiceTotal = new Decimal(0);
            for (let payment of item.invoice_payments) {
              invoiceTotal = invoiceTotal.plus(new Decimal(payment.amount));
            }
            if (invoiceTotal.gt(new Decimal(0))) {
              this.$toast('danger', this.$i18n.t('You can not cancel invoice with active payment, cancel payment first.'));
              return;
            }

          }
        }

        const itemsArray = Array.from(items)

        if (this.$hasPermission('auction_results_auction') && invoices_types.has('auction')) {
          this.showCancelInvoiceModal({
            source: ACTION_SHOW_CANCEL_INVOICE_MODAL,
            selectedItems: itemsArray
          })
          this.$refs.invoiceTable.clearSelected();
        } else {
          let requestData = itemsArray.map(el => ({
            invoice_uuid: el.uuid,
            status: 'cancelled'
          }))
          let success = await this.$confirm(
            this.$i18n.t("Are you sure you want to mark following invoice(s) as cancelled?"),
            this.$i18n.t('Invoice(s) were successfully marked cancelled'),
            InvoicesService.batchCancelInvoices,
            requestData);
          if (success) {
            this.getTableData();
            this.$refs.invoiceTable.clearSelected();
          }
        }
      },
      async onAllocateOverpaymentClick(invoice) {
        if (!['manual', 'auction', 'service'].includes(invoice.type)) {
          this.$errorDialog(this.$i18n.t('You can only allocate payment from auction, manual or service invoice.'))
          return
        }
        this.showAllocatePaymentModal({
          source: SOURCE_INVOICES_TABLE,
          invoiceUuid: invoice.uuid
        })
      },
      onPaymentsClick(invoice) {
        this.showPaymentListModal({
          source: SOURCE_INVOICES_TABLE,
          invoice,
        })
      },
      async onSendOverdueEmailClick(selectedItems, isFirst, isThird = false) {
        let invalidItems = [];
        selectedItems.forEach(item => {
          if (item.is_credited || item.type === INVOICE_TYPE_CREDIT_NOTE || (item.status !== 'unpaid' && item.shipping_status !== 'unpaid')) {
            invalidItems.push(item);
          }
        });

        if (invalidItems.length) {
          let errorMsg;
          if (isFirst) {
            errorMsg = 'Cannot send overdue email(s) for following invoices:';
          } else {
            errorMsg = isThird
              ? 'Cannot send third overdue email(s) for following invoices:'
              : 'Cannot send second overdue email(s) for following invoices:';
          }

          this.showErrorDialog(
            invalidItems,
            this.$i18n.t(errorMsg),
            this.$i18n.t('Please make sure that all of the selected invoices are unpaid.')
          );
        } else {
          let successMessage;
          let confirmMessage;

          if (isFirst) {
            successMessage = this.$i18n.t('Sending overdue emails has successfully started');
            confirmMessage = this.$i18n.t('Are you sure you want to send overdue invoice email(s) to {count} customer(s)?', {count: selectedItems.size});
          } else {
            successMessage = isThird
              ? this.$i18n.t('Sending third overdue emails has successfully started')
              : this.$i18n.t('Sending second overdue emails has successfully started');
            confirmMessage = isThird
              ? this.$i18n.t('Are you sure you want to send third overdue invoice email(s) to {count} customer(s)?', {count: selectedItems.size})
              : this.$i18n.t('Are you sure you want to send second overdue invoice email(s) to {count} customer(s)?', {count: selectedItems.size});
          }

          const params = {
            successMessage: successMessage,
            confirmMessage: confirmMessage,
            notificationFn: InvoicesService.sendOverdueEmails,
          };

          await this.showEmailConfirmModal(selectedItems, params, {is_first: isFirst, is_third: isThird});
        }
      },
      async onSendUnpaidEmailClick(selectedItems) {
        let invalidItems = [];
        selectedItems.forEach(item => {
          if (['cancelled'].includes(item.status) || item.is_credited || item.type === INVOICE_TYPE_CREDIT_NOTE) {
            invalidItems.push(item);
          }
        });

        if (invalidItems.length) {
          this.showErrorDialog(invalidItems,
            this.$i18n.t('Cannot send unpaid email(s) for following invoices:'),
            this.$i18n.t('Please make sure, that all of the selected invoices are unpaid.'))
        } else {
          const params = {
            successMessage: this.$i18n.t('Sending unpaid emails has successfully started'),
            confirmMessage: this.$i18n.t("Are you sure you want to send unpaid invoice email(s) to customers for {count} invoice(s)?", {count: selectedItems.size}),
            notificationFn: InvoicesService.sendUnpaidEmails,
          }

          await this.showEmailConfirmModal(selectedItems, params);
        }
      },
      async onResendInvoiceClick(selectedItems) {
        let invalidItems = [];
        selectedItems.forEach(item => {
          if (['cancelled'].includes(item.status) || item.is_credited || item.type === INVOICE_TYPE_CREDIT_NOTE) {
            invalidItems.push(item);
          }
        });

        if (invalidItems.length) {
          this.showErrorDialog(invalidItems,
            this.$i18n.t('Cannot resend following invoices:'),
            this.$i18n.t('Please make sure, that all of the selected invoice(s) are available.')
          )
        } else {
          const params = {
            successMessage: this.$i18n.t('Resending invoices has successfully started'),
            confirmMessage: this.$i18n.t("Are you sure you want to resend invoice email(s) to customers for {count} invoice(s)?", {count: selectedItems.size}),
            notificationFn: InvoicesService.resendInvoices,
          }
          await this.showEmailConfirmModal(selectedItems, params);
        }
      },
      showErrorDialog(items, message, footerMessage) {
        const dataArr = [];
        dataArr.push(this.$createElement('h5', {class: "mb-0"}, message));
        for (let item in items) {
          const listItemsArr = [];
          listItemsArr.push(this.$createElement('li', {}, items[item].invoice_no));
          dataArr.push(this.$createElement('ul', {class: "mb-0"}, listItemsArr));
        }
        dataArr.push(this.$createElement('h5', {class: "mb-0"}, footerMessage));
        this.$errorDialog(this.$createElement('div', {}, dataArr), this.$i18n.t('Validation Error'));
      },
      onOverdueProgressNotificationReceive(data) {
        if (data.status === 'finished') {
          this.$toast('success', this.$i18n.t("Sending overdue emails has successfully finished"));
          this.progressValue.overdue = null;
        } else if (data.status === 'processing') {
          this.progressValue.overdue = parseInt(Math.round((data.successful + data.failed / data.total) * 100))
        }
      },
      onUnpaidProgressNotificationReceive(data) {
        if (data.status === 'finished') {
          this.$toast('success', this.$i18n.t("Sending unpaid emails has successfully finished"));
          this.progressValue.unpaid = null;
        } else if (data.status === 'processing') {
          this.progressValue.unpaid = parseInt(Math.round((data.successful + data.failed / data.total) * 100))
        }
      },
      getDataObject(invoice) {
        if (invoice.status === 'cancelled' || invoice.is_credited) {
          return invoice.data;
        } else if (invoice.is_credit_note && invoice.credited_invoice) {
          return invoice.credited_invoice.data;
        }
        return invoice;
      },
      async onMarkAsDispatchedClick(selectedItems) {
        let data = {invoice_uuids: []};

        for (let invoice of selectedItems) {
          if (invoice.shipping_status !== 'paid') {
            this.$errorDialog(this.$i18n.t('Please select invoice(s) that have shipping paid.'));
            return;
          }
          if (invoice.status === 'cancelled') {
            this.$errorDialog(this.$i18n.t('You can not dispatch cancelled invoice(s)'));
            return;
          }
        }

        let success = await this.$confirm(this.$i18n.t("Are you sure you want to mark following invoice(s) as dispatched?"));
        if (!success) return;

        selectedItems.forEach(item => data.invoice_uuids.push(item.uuid));
        if (!(await this.handleReservationProposals(data.invoice_uuids))) return

        let response = await InvoicesService.markInvoicesAsDispatched(data);
        if (response.success) {
          this.$toast('success', this.$i18n.t("Invoice(s) were marked as dispatched"));
          this.getTableData();
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        }

      },
      async onUnmarkAsDispatchedClick(selectedItems) {
        let data = {invoice_uuids: []};

        for (let invoice of selectedItems) {
          if (!invoice.is_dispatched) {
            this.$errorDialog(this.$i18n.t('Please select only dispatched invoice(s)'));
            return;
          }
          if (invoice.status === 'cancelled') {
            this.$errorDialog(this.$i18n.t('You can not dispatch cancelled invoice(s)'));
            return;
          }
        }

        let success = await this.$confirm(this.$i18n.t("Are you sure you want to remove dispatch status from following invoices?"));
        if (!success) return;

        selectedItems.forEach(item => data.invoice_uuids.push(item.uuid));
        if (!(await this.handleReservationProposals(data.invoice_uuids))) return

        let response = await InvoicesService.unmarkInvoicesAsDispatched(data);
        if (response.success) {
          this.$toast('success', this.$i18n.t("Invoice(s) were unmarked as dispatched"));
          this.getTableData();
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        }
      },
      async onCollectedClick(selectedItems) {
        selectedItems = [...selectedItems];
        for (let item of selectedItems) {
          if (item.status === 'cancelled') {
            this.$toast('danger', this.$i18n.t('You can not collect item(s) from cancelled invoice'));
            return;
          }
          if (item.status === 'credited') {
            this.$toast('danger', this.$i18n.t('You can not collect item(s) from credited invoice'));
            return;
          }
          if (item.type === 'deposit') {
            this.$toast('danger', this.$i18n.t('You can not collect item(s) from deposit invoice(s)'));
            return;
          }
          if (item.type === 'service') {
            this.$toast('danger', this.$i18n.t('You can not collect item(s) from service invoice(s)'));
            return;
          }
          if (item.is_credit_note) {
            this.$toast('danger', this.$i18n.t('You can not collect item(s) from credit note invoice'));
            return;
          }
          if (item.is_credited) {
            this.$toast('danger', this.$i18n.t('You can not collect item(s) from credited invoice'));
            return;
          }
        }
        const uuids = selectedItems.map(i => i.uuid);

        if (!(await this.handleReservationProposals(uuids))) return

        const response = await this.markAllItemsCollected(uuids)
        if (response.success) {
          this.$toast('success', this.$t('Invoice item(s) was successfully collected'));
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        } else {
          this.$toast('danger', this.$t('Invoice item(s) could not be collected'));
        }
      },
      async onApproveClick(selectedItems) {
        selectedItems = [...selectedItems];
        for (let item of selectedItems) {
          if (item.status === 'cancelled') {
            this.$toast('danger', this.$i18n.t('You can not approve cancelled invoice'));
            return;
          }
          if (item.status === 'credited') {
            this.$toast('danger', this.$i18n.t('You can not approve credited invoice'));
            return;
          }
          // if (item.is_credit_note) {
          //   this.$toast('danger', this.$i18n.t('You can not approve credit note invoice'));
          //   return;
          // }
          if (item.is_credited) {
            this.$toast('danger', this.$i18n.t('You can not approve credited invoice'));
            return;
          }
        }
        const uuids = selectedItems.map(i => {
          return {uuid: i.uuid, approved: true}
        });

        let success = await this.$confirm(
          this.$i18n.t("Are you sure you want to approve following invoice(s)?"),
          this.$i18n.t('Invoice(s) were successfully approved'),
          InvoicesService.approveInvoices,
          uuids
        );

        if (success) {
          this.getTableData();
          this.$refs.invoiceTable.clearSelected();
        }
      },
      async onUndoApproveClick(selectedItems) {
        selectedItems = [...selectedItems];
        let invoice_nos = []
        for (let item of selectedItems) {
          invoice_nos.push(item.invoice_no)
          if (item.status === 'cancelled') {
            this.$toast('danger', this.$i18n.t('You cannot undo invoice approval for cancelled invoice'));
            return;
          }
          // if (item.status === 'credited') {
          //   this.$toast('danger', this.$i18n.t('You cannot undo invoice approval for credited invoice'));
          //   return;
          // }
          // if (item.is_credit_note) {
          //   this.$toast('danger', this.$i18n.t('You cannot undo invoice approval for credit note invoice'));
          //   return;
          // }
          // if (item.is_credited) {
          //   this.$toast('danger', this.$i18n.t('You cannot undo invoice approval for credited invoice'));
          //   return;
          // }
        }
        const uuids = selectedItems.map(i => {
          return {uuid: i.uuid, approved: false}
        });

        let success = await this.$confirm(
          this.$i18n.t(`You are trying to undo the approval of the following invoice(s). Invoices: ${invoice_nos.join(', ')}. Do you wish to proceed?`),
          this.$i18n.t('Invoice(s) approval was successfully undone.'),
          InvoicesService.approveInvoices,
          uuids
        );

        if (success) {
          this.getTableData();
          this.$refs.invoiceTable.clearSelected();
        }
      },
      async onMarkItemsCollectedClick(invoice) {
        await this.getMarkItemsCollectedModalObject({invoice});
        this.showMarkItemsCollectedModal({
          source: SOURCE_INVOICES_TABLE,
        });
      },
      async addBatchInvoiceGenerationProgressIfNotExist() {
        if (!this.batchInvoiceGenerationProgress) {
          this.batchInvoiceGenerationProgress = await this.addProgress(
            this.$i18n.t("Unpaid invoice generation for sale {sale_no}", {sale_no: this.sale.sale_no})
          );
        }
        return true
      },
      removeProgressIfExist() {
        if (this.batchInvoiceGenerationProgress) {
          this.removeProgress(this.batchInvoiceGenerationProgress.uuid)
          this.batchInvoiceGenerationProgress = null;
        }
      },
      async onBatchCreateNotificationReceive(data) {
        await this.addBatchInvoiceGenerationProgressIfNotExist()
        this.updateProgressValue({
          progressValue: parseInt(Math.round(data.successful / data.total * 100)),
          uuid: this.batchInvoiceGenerationProgress.uuid
        });
        if (data.is_finished) {
          if (!data.errors) {
            this.$toast('success', this.$i18n.t("Unpaid invoices generation has successfully finished"));
          }
          this.removeProgressIfExist();
          this.getTableData()
        }
        if (data.errors) {
          if (data.errors.non_field_errors) {
            const listItemsArr = [];
            data.errors.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t("Result"));
          } else {
            this.$errorDialog(this.$i18n.t("Something went wrong while batch generating unpaid invoices. Please, try again later."))
          }
        }
      },
      async onSyncWithAccountingSystemClick() {
        this.showDateRangeModal();
      },
      async onSyncWithAccountingSystemDataReceive(data) {
        for (let process of data) {
          if (process.object_type !== "invoice") {
            continue;
          }
          let progress = this.processIdToProgressMap[process.id];
          if (process.status === 'finished') {
            if (progress) {
              this.removeProgress(progress.uuid);
              delete this.processIdToProgressMap[process.id]
            }
            if (process.errors && Object.keys(process.errors).length > 0) {
              if (this.currentUser.uuid === process.created_by_uuid) {
                this.handleAccountingErrors(process)
              }
            } else {
              this.$toast('success', this.$i18n.t('Synchronization with accounting system successfully finished'))
            }
          } else {
            if (!progress) {
              progress = await this.addProgress(this.$i18n.t("Synchronizing with accounting system, Started by {created_by} at {created_at}", {
                created_by: process.created_by,
                created_at: this.$options.filters.toDateTime(process.created_at),
              }))
            }
            progress = {
              ...progress,
              progressValue: parseInt(Math.round(((process.success_count + process.failed_count) / process.invoice_count) * 100))
            }
            this.updateProgressValue(progress);
            this.processIdToProgressMap[process.id] = progress;
          }
        }
      },
      handleAccountingErrors(process) {
        let errorData = [];
        for (let item of process.errors) {
          Object.keys(item).forEach(key => {
            let errorMessage;
            if (key === "non_field_errors") {
              errorMessage = this.$i18n.t(item[key]);
            } else {
              errorMessage = this.$i18n.t("Invoice: {invoiceNo}. Error: {error}", {
                invoiceNo: key,
                error: item[key]
              });
            }
            errorData.push(errorMessage)
          });
        }
        const listItemsArr = [];
        errorData.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
        this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t("Errors on invoice synchronization"));
      },
      async onViewActivitiesClick(invoice) {
        let response = await InvoicesService.getInvoice(invoice.uuid)
        this.setActivitiesParams({object: response.body, source: 'invoice', limit: 9})
        this.showActivitiesModal()
      },
      async onDateRangeSubmit(dateRangePickerSelection) {
        let response = await AccountingService.syncWithAccountingSystem({
          ...dateRangePickerSelection,
          ...this.accountingSystemParams,
          'object_type': 'invoice'
        });
        if (response.success) {
          const message = response.body.message ? response.body.message : 'Invoice synchronization with accounting system has successfully started';
          this.$toast('success', this.$i18n.t(message));
          this.$refs["date-range-modal"].onHidden();
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        }
      },
      onInvoiceNotificationPdfsPreviewGenerationProgressReceived(data) {
        NotificationPdfPreviewService.onNotificationPdfsPreviewGenerationProgressReceived(data, this);
      },
      async onPreviewAllClick(selectedItems) {
        let invoice_uuids = [];
        selectedItems.forEach(item => invoice_uuids.push(item.uuid));
        let response = await NotificationPdfPreviewService.previewPdfs({
          modelType: 'invoice',
          module: this,
          pdfLoadFn: InvoicesService.generateMergedPdfForInvoices,
          pdfLoadData: {
            invoice_uuids: invoice_uuids,
          },
        });
        if (!response.success && response.body && response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0])
        }
      },
      onCreateCreditNoteClick(selectedItems) {
        selectedItems = [...selectedItems];
        const object = selectedItems[0];

        if (object.status === 'cancelled') {
          this.$toast('danger', this.$i18n.t('You can not create credit note from cancelled invoice'));
          return;
        }
        if (object.type === 'deposit') {
          this.$toast('danger', this.$i18n.t('You can not create credit note from deposit invoice'));
          return;
        }
        if (object.is_credit_note) {
          this.$toast('danger', this.$i18n.t('You can not create credit note from credit note invoice'));
          return;
        }
        if (object.is_credited) {
          this.$toast('danger', this.$i18n.t('You can not create credit note from credited invoice'));
          return;
        }
        if (object.is_legacy) {
          this.$toast('danger', this.$i18n.t('You can not create credit note from legacy invoice'));
          return;
        }

        this.showCreditNoteModal({object: object, source: 'invoice'});
        this.getCreditNoteInvoiceModalObject({invoice: object});
      },
      async onSendCreditNoteEmailClick(selectedItems) {
        let notCreditedItems = [];
        selectedItems.forEach(item => {
          if (item.type !== INVOICE_TYPE_CREDIT_NOTE) {
            notCreditedItems.push(item);
          }
        });

        if (notCreditedItems.length) {
          this.showErrorDialog(notCreditedItems,
            this.$i18n.t('Cannot send credit note email(s) for following invoices:'),
            this.$i18n.t('Please make sure, that all of the selected items are credit notes.'));
        } else {
          const params = {
            successMessage: this.$i18n.t('Sending credit note emails has successfully started'),
            confirmMessage: this.$i18n.t('Are you sure you want to send credit note invoice email(s) to customers for {count} invoice(s)?', {count: selectedItems.size}),
            notificationFn: InvoicesService.sendCreditNoteEmails,
          }

          await this.showEmailConfirmModal(selectedItems, params);
        }
      },
      async onSendCollectionAppointmentNotificationClick(selectedItems) {
        let unpaid = [];
        let notAuction = [];
        let alreadySent = [];
        selectedItems.forEach(item => {
          if (item.type !== INVOICE_TYPE_AUCTION) {
            notAuction.push(item);
          } else if (item.status !== 'paid') {
            unpaid.push(item);
          } else if (item.collection_appointment_notification_sent_at) {
            alreadySent.push(item.invoice_no);
          }
        });

        if (notAuction.length) {
          this.showErrorDialog(notAuction,
            this.$i18n.t('Unable to send Collection Appointment email. This action is only available for Auction invoices. Please select a valid invoice type.'),
            '');
          return;
        }

        if (unpaid.length) {
          this.showErrorDialog(unpaid,
            this.$i18n.t('Cannot send collection appointment email(s) for following invoice(s):'),
            this.$i18n.t('Please make sure, that all of the selected invoices have the status paid.'));
          return;
        }

        let message = '';
        if (alreadySent.length) {
          let message1 = this.$i18n.t('A Collection Appointment notification has already been sent for the following invoice(s):');
          let message2 = this.$i18n.t('Would you like to send the notification again?');
          message = message1 + ' ' + alreadySent.join(', ') + '. ' + message2;
        } else {
          message = this.$i18n.t("Are you sure you want to send collection appointment email(s) to customers for {count} invoice(s)?", {count: selectedItems.size});
        }

        let data = {'invoice_uuids': []};
        selectedItems.forEach(item => data.invoice_uuids.push(item.uuid));
        let checkRes = await InvoicesService.sendCollectionAppointmentEmails({...data, check: true});
        let typeConfirm = false;
        if (!checkRes.success) {
          console.log(checkRes.body);
          if (checkRes.body.check_failed) {
            message = this.$i18n.t('Collection Appointment details are missing at the sale level.');
            message = message + ' ' + checkRes.body.check_failed.join(', ');
            typeConfirm = true;
          } else {
            if (checkRes.body.non_field_errors) {
              this.$errorDialog(checkRes.body.non_field_errors[0]);
            } else if (checkRes.toastError) {
              this.$errorDialog(checkRes.toastError);
            } else {
              this.$errorDialog(this.$i18n.t('Unable to send'));
            }
          }
        }
        let confirmArgs = [
          message,
          null,
          null,
          null,
          this.$i18n.t('Please Confirm'),
          this.$i18n.t('Send'),
          this.$i18n.t('Cancel')
        ];
        if (typeConfirm) {
          confirmArgs.push(
            null,
            {
              label: this.$i18n.t("Please type 'confirm' to proceed"),
              text: 'confirm'
            }
          );
        }

        let confirmed = await this.$confirm(...confirmArgs);
        if (confirmed) {
          let res = await InvoicesService.sendCollectionAppointmentEmails(data);
          if (res.success) {
            this.$toast('success', this.$i18n.t('Sending collection appointment emails has successfully started'));
          } else {
            console.log(res.body);
            if (res.body.non_field_errors) {
              this.$errorDialog(res.body.non_field_errors[0]);
            } else if (res.toastError) {
              this.$errorDialog(res.toastError);
            } else {
              this.$errorDialog(this.$i18n.t('Unable to send'));
            }
          }
        }
      },
      async showEmailConfirmModal(selectedItems, params, notificationFnParameters = {}) {
        let data = notificationFnParameters;
        data['invoice_uuids'] = [];
        selectedItems.forEach(item => data.invoice_uuids.push(item.uuid));

        const {successMessage, confirmMessage, notificationFn} = params;


        let handlerData = {
          modelType: 'invoice',
          module: this,
          pdfLoadFn: InvoicesService.generateMergedPdfForInvoices,
          pdfLoadData: {
            invoice_uuids: data.invoice_uuids,
          },
          notificationFn: notificationFn,
          notificationFnParams: data,
          notificationFnSuccessMessage: successMessage,
        }

        await this.$confirm(
          confirmMessage,
          null,
          NotificationPdfPreviewService.sendEmailNotifications,
          {
            notificationFn: notificationFn,
            data: data,
            successMessage: successMessage,
            module: this
          },
          this.$i18n.t('Please Confirm'),
          this.$i18n.t('Send'),
          this.$i18n.t('Cancel'),
          [{
            submitBtnText: this.$i18n.t('Preview and Send'),
            handler: NotificationPdfPreviewService.previewPdfs,
            handlerParams: handlerData,
            indicateRefusal: true
          }],
        );
      },
      disabledInvoice(invoice) {
        return invoice.status === 'cancelled' || invoice.is_dispatched || invoice.is_credited;
      },
      async onExportClick(type, isPayment = false) {
        let success = await this.$confirm(
          this.$i18n.t("{count} row(s) will be exported. Do you want to continue?", {count: this.tableData.pagination.total}))
        if (!success) {
          return
        }

        const {
          prepareRequestExportsParams,
          schedulePaymentExportInvoices,
          scheduleExportInvoices
        } = ExportsService

        const requestParams = await prepareRequestExportsParams(this.exportAttributes, "invoices", type)
        const response = isPayment
          ? await schedulePaymentExportInvoices(requestParams)
          : await scheduleExportInvoices(requestParams)

        if (response.success) {
          this.$toast('success', this.$i18n.t('Export generation has successfully started'))
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          }
        }
      },
      onShowTransactionMoreClick(item) {
        this.showTransactionExportsModal({
          invoice: item,
          source: SOURCE_INVOICE_TRANSACTION_EXPORTS
        })
      },
      onShowPaymentMoreClick(item) {
        this.showPaymentExportsModal({
          invoice: item,
          source: SOURCE_INVOICE_PAYMENT_EXPORTS
        })
      },
      async handleReservationProposals(invoice_uuids) {
        const reservation_proposals = (await LotsService.getLatestReservationProposals({
          invoice_uuids: invoice_uuids,
          suggestions: ['reoffer'],
        })).body
        if (reservation_proposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(reservation_proposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) {
            this.loading = false
            return false
          }
        }
        return true
      },
      onGenerateBatchPaymentsClick() {
        this.showInvoiceBatchPaymentModal({
          source: SOURCE_INVOICES_TABLE
        })
      },
      isAutoPaymentEnabled(selectedItems) {
        if (!selectedItems || selectedItems.size === 0) return false;
        for (let item of selectedItems) {
          if (item.computed_auto_payment_status !== 'awaiting_approval') {
            return false;
          }
        }
        return true;
      },
      formatAutoPaymentStatus(invoice) {
        const status = invoice.computed_auto_payment_status;
        if (status === 'grace_period') return this.$t('Grace Period');
        if (status === 'awaiting_approval') return this.$t('Awaiting Approval');
        if (status === 'charge_attempted') return this.$t('Charge Attempted');
        if (status === 'n_a') return this.$t('N/A');
        return status;
      },
      async onApproveAutoPaymentClick(selectedItems) {
        if (!this.isAutoPaymentEnabled(selectedItems)) return;

        const uuids = [];
        selectedItems.forEach(item => uuids.push(item.uuid));

        const confirmed = await this.$confirm(
          this.$t('This will initiate a one-time charge attempt for selected invoice(s). Buyers\' cards on file will be charged. This action cannot be undone. Please type "Confirm Payments" below to confirm this action.'),
          null,
          InvoicesService.approveAutoPayment,
          {uuids},
          this.$t('Approve Auto-Payment?'),
          this.$t('Confirm'),
          this.$t('Cancel'),
          null,
          {label: this.$t('Type "Confirm Payments" to proceed'), text: 'Confirm Payments'},
          null,
          'danger'
        );

        if (confirmed) {
          this.$refs.invoiceTable.clearSelected();
        }
      },
      async addAutoPaymentProgressIfNotExist() {
        if (!this.autoPaymentProgress) {
          this.autoPaymentProgress = await this.addProgress(
            this.$i18n.t("Processing auto-payments")
          );
        }
        return true;
      },
      removeAutoPaymentProgressIfExist() {
        if (this.autoPaymentProgress) {
          this.removeProgress(this.autoPaymentProgress.uuid);
          this.autoPaymentProgress = null;
        }
      },
      async onAutoPaymentProgressReceive(data) {
        await this.addAutoPaymentProgressIfNotExist();
        this.updateProgressValue({
          progressValue: parseInt(Math.round(data.processed / data.total * 100)),
          uuid: this.autoPaymentProgress.uuid
        });
        if (data.is_finished) {
          if (data.errors && data.errors.non_field_errors) {
            const listItemsArr = [];
            data.errors.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)));
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Auto-Payment Result'));
          } else {
            this.$toast('success', this.$i18n.t('Auto-payment process has finished'));
          }
          this.removeAutoPaymentProgressIfExist();
          this.getTableData();
        }
      },
      async onInvoiceToggleExtraInfo(invoice, input) {
        if (invoice.status === 'cancelled') {
          this.$toast('danger', this.$i18n.t('You can not alter cancelled invoice'));
          return;
        }
        if (invoice.is_credited) {
          this.$toast('danger', this.$i18n.t('You can not alter credited invoices'));
          return;
        }
        let value = true;
        let confirmText = this.$i18n.t(`Are you sure you want to check "{label}"`, {label: input.label});
        if (invoice && invoice.extra_info && invoice.extra_info[input.key]) {
          confirmText = this.$i18n.t(`Are you sure you want to uncheck "{label}"`, {label: input.label});
          value = false;
        }
        const confirm = await this.$confirm(confirmText)
        if (!confirm) {
          return;
        }
        this.$toast('success', this.$i18n.t('Invoice generation has started'));
        const response = await InvoicesService.updateInvoiceExtraInfo({uuid: invoice.uuid, [input.key]: value});
        if (response.success) {
          this.$toast('success', this.$i18n.t('Invoice generation has successfully completed.'));
          this.getTableData();
        } else {
          this.$toast('error', response.message)
        }
      },
    },
    mounted() {
      this.getTableData();
      if (!this.hideAction(ACTION_BATCH_INVOICE_GENERATE)) {
        this.$ws.listen(`invoices.${WS_BATCH_INVOICE_GENERATION_PROGRESS}`, this.onBatchCreateNotificationReceive);
      }
      if (!this.hideAction(ACTION_SEND_OVERDUE_EMAILS)) {
        this.$ws.listen(`invoices.${WS_ON_OVERDUE_NOTIFICATION_PROGRESS}`, this.onOverdueProgressNotificationReceive);
      }
      if (!this.hideAction(ACTION_SEND_UNPAID_EMAILS)) {
        this.$ws.listen(`invoices.${WS_ON_UNPAID_NOTIFICATION_PROGRESS}`, this.onUnpaidProgressNotificationReceive);
      }
      if (!this.hideAction(ACTION_SYNC_WITH_ACCOUNTING_SYSTEM)) {
        this.$ws.listen(`invoices.${WS_ACCOUNTING_SYNC_PROGRESS}`, this.onSyncWithAccountingSystemDataReceive);
      }
      if (!(this.hideAction(ACTION_SEND_UNPAID_EMAILS) || this.hideAction(ACTION_SEND_OVERDUE_EMAILS) || this.hideAction(ACTION_SEND_SECOND_OVERDUE_EMAILS) || this.hideAction(ACTION_SEND_THIRD_OVERDUE_EMAILS))) {
        this.$ws.listen(`invoices.${WS_INVOICES_MERGED_PDF_PREVIEW_GENERATION_PROGRESS}`,
          this.onInvoiceNotificationPdfsPreviewGenerationProgressReceived
        );
      }
      if (!this.hideAction(ACTION_APPROVE_AUTO_PAYMENT)) {
        this.$ws.listen(`invoices.${WS_BATCH_AUTO_PAYMENT_PROGRESS}`, this.onAutoPaymentProgressReceive);
      }
    },
    beforeDestroy() {
      this.removeProgressIfExist();
      this.removeAutoPaymentProgressIfExist();
      this.$ws.remove(`invoices.${WS_ON_OVERDUE_NOTIFICATION_PROGRESS}`);
      this.$ws.remove(`invoices.${WS_ON_UNPAID_NOTIFICATION_PROGRESS}`);
      this.$ws.remove(`invoices.${WS_BATCH_INVOICE_GENERATION_PROGRESS}`);
      this.$ws.remove(`invoices.${WS_ACCOUNTING_SYNC_PROGRESS}`);
      this.$ws.remove(`invoices.${WS_INVOICES_MERGED_PDF_PREVIEW_GENERATION_PROGRESS}`);
      this.$ws.remove(`invoices.${WS_BATCH_AUTO_PAYMENT_PROGRESS}`);
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .total-notes {
    top: -7px;
    right: 12px;
    margin: 0;
    border-radius: 100%;
    padding: 3px 6px;
    font-size: 10px;
    position: absolute;
    background-color: #1ec973;
    color: white;
  }

  .invoices-table {
    min-height: 25em;
  }

  .artisio-table {
    /deep/ {
      .invoice-no-col {
        min-width: 100px;
      }

      .type-col {
        min-width: 70px;
      }

      .sale-no-col {
        min-width: 70px;
      }

      .customer-uuid-col {
        min-width: 130px;
      }

      .buyer-vat-col {
        min-width: 130px;
      }

      .amount-col {
        min-width: 210px;
      }

      .items-col {
        min-width: 70px;
      }

      .status-col {
        min-width: 90px;
      }

      .date-col {
        min-width: 100px;
      }

      .shipping-status-col {
        min-width: 90px;
      }

      .collected-col {
        min-width: 120px;
      }

      .complaint-status-col {
        min-width: 100px;
      }

      .actions-col {
        width: 70px;
      }

      .overdue-notification-not-sent-col {
        min-width: 100px;
      }

      .unpaid-notification-not-sent-col {
        min-width: 100px;
      }

      .is-overdue-col {
        min-width: 100px;
      }

      .complaint-col {
        min-width: 100px;
      }

      .auto-payment-status-col {
        min-width: 150px;
      }

      .synced-at-col {
        min-width: 100px;
      }

      .created-data-col {
        min-width: 100px;
      }

      .updated-data-col {
        min-width: 100px;
      }

      .customer_company_name-col {
        min-width: 100px;
      }

      .shipping_tracking_code-col {
        min-width: 100px;
      }

      .payment_methods_col {
        min-width: 120px;
      }
    }
  }

  /deep/ .dropdown-menu.show {
    max-height: 280px;
    overflow: auto;
  }
</style>

